import { atom, PrimitiveAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { WorkspaceLayoutPane } from '@/components/DataWorkspace/utils'

const STORAGE_KEY = 'workspacePaneState'

// Default values
const defaultValues: Record<WorkspaceLayoutPane, boolean> = {
  queryOutput: true,
  schema: false,
  query: true,
  queryHistory: false,
  assignment: true,
  solution: true,
  top: true,
  bottom: true,
}

// Load all values from localStorage
export function loadWorkspaceStatesFromStorage(): Partial<Record<WorkspaceLayoutPane, boolean>> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed
    }
  } catch (error) {
    console.warn('Failed to load workspace pane state from localStorage:', error)
  }
  return {}
}

// Save all values to localStorage
function saveToStorage(values: Record<WorkspaceLayoutPane, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values))
  } catch (error) {
    console.warn('Failed to save workspace pane state to localStorage:', error)
  }
}

// Load stored values before applying defaults
const storedValues = loadWorkspaceStatesFromStorage()

// Merge stored values with defaults (stored values take precedence)
export const initialWorkspacePaneValues: Record<WorkspaceLayoutPane, boolean> = {
  ...defaultValues,
  ...storedValues,
}

// Create base atoms first
const baseAtoms: Record<WorkspaceLayoutPane, PrimitiveAtom<boolean>> = {
  queryOutput: atom(initialWorkspacePaneValues.queryOutput),
  schema: atom(initialWorkspacePaneValues.schema),
  query: atom(initialWorkspacePaneValues.query),
  queryHistory: atom(initialWorkspacePaneValues.queryHistory),
  assignment: atom(initialWorkspacePaneValues.assignment),
  solution: atom(initialWorkspacePaneValues.solution),
  top: atom(initialWorkspacePaneValues.top),
  bottom: atom(initialWorkspacePaneValues.bottom),
}

// Create synced atoms that save to localStorage on write
function createSyncedAtom(key: WorkspaceLayoutPane): PrimitiveAtom<boolean> {
  const baseAtom = baseAtoms[key]
  
  return atom(
    (get) => get(baseAtom),
    (get, set, newValue: boolean) => {
      set(baseAtom, newValue)
      // Save all current values to localStorage
      const allValues: Record<WorkspaceLayoutPane, boolean> = {
        queryOutput: get(baseAtoms.queryOutput),
        schema: get(baseAtoms.schema),
        query: get(baseAtoms.query),
        queryHistory: get(baseAtoms.queryHistory),
        assignment: get(baseAtoms.assignment),
        solution: get(baseAtoms.solution),
        top: get(baseAtoms.top),
        bottom: get(baseAtoms.bottom),
      }
      allValues[key] = newValue
      saveToStorage(allValues)
    }
  ) as PrimitiveAtom<boolean>
}

// Create synced atoms with initial values from storage
export const workspacePaneQueryOutputAtom = createSyncedAtom('queryOutput')
export const workspacePaneSchemaAtom = createSyncedAtom('schema')
export const workspacePaneQueryAtom = createSyncedAtom('query')
export const workspacePaneQueryHistoryAtom = createSyncedAtom('queryHistory')
export const workspacePaneAssignmentAtom = createSyncedAtom('assignment')
export const workspacePaneSolutionAtom = createSyncedAtom('solution')
export const workspacePaneTopAtom = createSyncedAtom('top') // dummy atom
export const workspacePaneBottomAtom = createSyncedAtom('bottom') // dummy atom

const paneAtomMap: Record<WorkspaceLayoutPane, PrimitiveAtom<boolean>> = {
  queryOutput: workspacePaneQueryOutputAtom,
  schema: workspacePaneSchemaAtom,
  query: workspacePaneQueryAtom,
  queryHistory: workspacePaneQueryHistoryAtom,
  assignment: workspacePaneAssignmentAtom,
  solution: workspacePaneSolutionAtom,
  top: workspacePaneTopAtom,
  bottom: workspacePaneBottomAtom,
}

export const getPaneAtom = (key: WorkspaceLayoutPane): PrimitiveAtom<boolean> => {
  return paneAtomMap[key]
}

export const workspaceLayoutAtom = atomWithStorage<'split-top' | 'split-side'>('workspaceLayout', 'split-top')